/* ============================================================
 * Switss motion — globale reveal + parallax helper (kaart #118)
 * Geen externe libraries. Laad met defer in theme.liquid.
 *
 * 1) Reveal-on-scroll: elementen met class "sw-reveal" (modifiers
 *    --fade / --rise / --scale, optioneel data-sw-delay) krijgen
 *    "is-visible" zodra ze in beeld komen (IntersectionObserver).
 * 2) Motion-niveau per sectie: leest data-motion="full|subtle|none"
 *    van de dichtstbijzijnde section-wrapper ([data-motion] of
 *    .shopify-section) en zet .sw-motion-subtle / .sw-motion-none
 *    op die wrapper. "none" = direct zichtbaar, "subtle" = kortere/
 *    kleinere transities (CSS regelt de daadwerkelijke waarden).
 * 3) Hero-parallax: elementen met data-sw-parallax krijgen een
 *    translate/scale op scroll, geschaald binnen hun eigen sectie
 *    (requestAnimationFrame, passive listeners).
 * 4) Scroll-zoom: elementen met data-sw-scrollzoom (kaart #118, home-hero)
 *    zetten een CSS-variabele --sw-zoom-progress (0..1) op zichzelf op
 *    basis van scrollpositie (rAF, passive listener). Dit is de enige
 *    animatiemotor (animation-timeline: view() bleek onbetrouwbaar op
 *    het sticky/scrollstage-patroon en is verwijderd) — altijd actief
 *    tenzij prefers-reduced-motion. Geen scroll-hijacking: dit leest
 *    alleen scrollpositie, het scrollt nooit zelf.
 *    Binnen dezelfde stage togglen we ook data-sw-copy-hidden op het
 *    [data-sw-hero-copy]-blok (boodschap-estafette, polish kaart #118):
 *    de CSS faded het copy-blok al uit via --sw-zoom-progress, dit is
 *    alleen de pointer-events-guard zodra het nagenoeg onzichtbaar is.
 * 5) Fallback: geen IntersectionObserver-support of
 *    prefers-reduced-motion -> alles meteen zichtbaar, geen parallax,
 *    geen scroll-zoom.
 * 6) Koopchip OOS-scroll (kaart #118, Apple-banner-hero-ronde,
 *    sw-product-herozoom.liquid): klik op [data-sw-chip-oos-scroll]
 *    scrollt vloeiend naar het element met het ID in
 *    data-sw-chip-oos-target (de sw-oos-signup-sectie), i.p.v. een
 *    page-load/anchor-jump. Event delegation op document (eenmalig
 *    geregistreerd buiten init(), zie onderaan), dus werkt ook voor
 *    secties die na page-load via shopify:section:load bijkomen.
 * 7) Product-subnav (kaart #118, subnav-ronde, snippets/sw-product-
 *    subnav.liquid): Apple-stijl fixed balk op de 8 product-landingen.
 *    - Toont/verbergt de balk op dezelfde IntersectionObserver-drempel
 *      als voorheen de sticky cta-bar (hero-zoomstage boven de viewport
 *      verdwenen: entry.isIntersecting === false EN boundingClientRect.
 *      bottom < 0, zodat 'ie niet al bij page-load meteen actief is op
 *      een korte viewport).
 *    - Verbergt in dezelfde stap de koopchip (data-sw-chip-hidden) en
 *      toggelt "sw-subnav-hides-header" op .shopify-section-header,
 *      zodat er nooit twee balken tegelijk staan (geen aparte header-
 *      observer nodig: dezelfde trigger stuurt alle drie UI's aan).
 *    - Ankers zonder bestaand doel-element (Vergelijk/Combi deals zijn
 *      conditioneel: alleen op hub8/hub12 resp. m2/sw25/sw35/hub8/hub12)
 *      worden verborgen via [hidden] -- toekomstvast, geen aparte
 *      per-sectie booleans nodig.
 *    - Scroll-spy: IntersectionObserver op de doel-secties markeert de
 *      dichtstbijzijnde/bovenste zichtbare sectie als actief.
 *    - Reduced-motion: balk toont gewoon zonder animatie (CSS regelt de
 *      transitie-uitschakeling), logica hieronder blijft ongewijzigd.
 * 8) Subnav-hoogte (kaart #176, ronde 2): op mobiel wrapt .sw-subnav__inner
 *    een variabel aantal regels (title + kleur-dots + Koop-knop) afhankelijk
 *    van de schermbreedte, dus de werkelijke balkhoogte verschilt per
 *    toestel. Meet de echte hoogte (ResizeObserver, resize-listener als
 *    fallback) en zet 'm als --sw-subnav-h op de document-root, zodat CSS
 *    scroll-margin-top erbovenop kan tellen i.p.v. een vaste geraden
 *    waarde. Zelfde patroon als initHeroFold (--sw-phz-fold) hieronder.
 * ============================================================ */
(function () {
  "use strict";

  var reduceMotion = window.matchMedia
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;

  /* ---- Helper: vind de dichtstbijzijnde section-wrapper met motion-niveau ---- */
  function findMotionWrapper(el) {
    return el.closest("[data-motion]") || el.closest(".shopify-section") || null;
  }

  function applyMotionLevel(el) {
    var wrapper = findMotionWrapper(el);
    if (!wrapper) return "full";
    var level = wrapper.getAttribute("data-motion") || "full";
    if (level === "subtle") {
      wrapper.classList.add("sw-motion-subtle");
    } else if (level === "none") {
      wrapper.classList.add("sw-motion-none");
    }
    return level;
  }

  /* ---- 1 + 2) Reveal-on-scroll ----
     Reveal-fallback vangnet (fixronde klantpanel-1000, structureel bugrapport
     "sw-reveal blijft in tussentoestand hangen", RAID-gidskaart): naast de
     pure-CSS-animation-fallback in switss-tokens.css (elk .sw-reveal krijgt
     sowieso na ~2,5s een animatie naar zichtbaar als de is-visible-class er
     dan nog niet op staat) doet reCheckStuckReveals() hier een actieve
     tweede controle: elementen die al (gedeeltelijk) in beeld zijn maar
     de is-visible-class nog missen — bijvoorbeeld omdat de IO-callback door
     een layout-thrashing-edge-case nooit vuurde — krijgen 'm alsnog. Dit is
     dus een dubbel vangnet (CSS + JS), geen vervanging van elkaar: de CSS-
     fallback dekt ook het geval waarin JS zelf om wat voor reden dan ook
     nooit meer draait na de eerste init, de JS re-check maakt het element
     meteen (i.p.v. na 2,5s) zichtbaar zodra het al in beeld is. ---- */
  function initReveal() {
    var reveals = Array.prototype.slice.call(document.querySelectorAll(".sw-reveal"));
    if (!reveals.length) return;

    reveals.forEach(function (el) {
      var level = applyMotionLevel(el);
      var delay = el.getAttribute("data-sw-delay");
      if (delay && !isNaN(parseFloat(delay))) {
        el.style.transitionDelay = (parseFloat(delay) * 0.1) + "s";
      }
      if (level === "none") {
        el.classList.add("is-visible");
      }
    });

    if (reduceMotion || !("IntersectionObserver" in window)) {
      reveals.forEach(function (el) { el.classList.add("is-visible"); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.18, rootMargin: "0px 0px -8% 0px" });

    reveals.forEach(function (el) {
      if (!el.classList.contains("is-visible")) io.observe(el);
    });

    /* Re-check op load/resize: vangt het geval dat een element al in beeld
       staat (of kwam) maar de is-visible-class nog niet heeft — geen
       nieuwe observer nodig, alleen een directe geometrie-check tegen de
       huidige viewport. Lichtgewicht (geen scroll-listener: resize/load
       zijn de enige triggers, gecombineerd met de bestaande IO voor het
       normale scroll-pad) en idempotent (classList.add op een element dat
       al is-visible heeft, is een no-op). */
    function reCheckStuckReveals() {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      reveals.forEach(function (el) {
        if (el.classList.contains("is-visible")) return;
        var rect = el.getBoundingClientRect();
        var inView = rect.top < vh * 0.92 && rect.bottom > 0;
        if (inView) {
          el.classList.add("is-visible");
          io.unobserve(el);
        }
      });
    }

    window.addEventListener("load", reCheckStuckReveals);
    window.addEventListener("resize", reCheckStuckReveals, { passive: true });
  }

  /* ---- 3) Hero-parallax ---- */
  function initParallax() {
    if (reduceMotion) return;
    var items = Array.prototype.slice.call(document.querySelectorAll("[data-sw-parallax]"));
    if (!items.length) return;

    var entries = items.map(function (el) {
      var scope = el.closest("[data-motion]") || el.closest(".shopify-section") || el.parentElement;
      var strength = parseFloat(el.getAttribute("data-sw-parallax")) || 1;
      return { el: el, scope: scope || el, strength: strength };
    });

    var ticking = false;

    function update() {
      ticking = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      entries.forEach(function (entry) {
        var rect = entry.scope.getBoundingClientRect();
        /* progress 0 (sectie net onderin beeld) -> 1 (sectie scrollt uit beeld) */
        var progress = Math.min(1, Math.max(0, -rect.top / (vh * 0.9)));
        var translate = (progress * -28 * entry.strength).toFixed(1);
        var scale = (1 + progress * 0.10 * entry.strength).toFixed(3);
        entry.el.style.transform = "translateY(" + translate + "px) scale(" + scale + ")";
      });
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    update();
  }

  /* ---- 4) Scroll-zoom (home-hero, kaart #118) ---- */
  function initScrollZoom() {
    var stages = Array.prototype.slice.call(document.querySelectorAll("[data-sw-scrollzoom]"));
    if (!stages.length) return;

    /* zoom_enabled=false in de sectie-settings laat data-sw-scrollzoom
       alleen op de "true"-variant staan (zie home-hero.liquid); een
       uitgeschakelde stage heeft geen zoomstage-opmaak (min-height: 0)
       en hoeft niet geanimeerd te worden. */
    stages = stages.filter(function (el) {
      return el.getAttribute("data-zoom-enabled") === "true";
    });
    if (!stages.length) return;

    if (reduceMotion) return;

    stages.forEach(function (el) { el.setAttribute("data-zoom-active", "true"); });

    /* Boodschap-estafette (polish, kaart #118; TWEEDE fixronde
       klantpanel-1000, hertest 2026-07-04 — leesbaarheids-dal gemeten en
       vensters verschoven, zie home-hero.liquid voor de reken-uitleg):
       het copy-blok faded nu uit tussen progress .45 en .55 (CSS,
       --sw-copy-fade), NA het startpunt van de reveal (.35). Zodra het
       nagenoeg onzichtbaar is (>= .54, fade ~.9) zetten we pointer-events
       uit zodat er geen onklikbare-maar-blokkerende laag over de
       Switss®-reveal blijft liggen. Iets vóór het volledige .55-punt,
       ruim ná zichtbaar gebruik. */
    var COPY_HIDE_THRESHOLD = 0.54;

    /* ---- iPhone/WebKit-pin-fallback (iOS-foto-wissel-fix, 2026-07-18) ----
       De crossfade zelf (--sw-zoom-progress hieronder) is al
       motoronafhankelijk (rAF + getBoundingClientRect; animation-timeline is
       hier eerder al verwijderd, zie de dossier-comment bovenaan). Wat WEL
       motorafhankelijk is: het PINNEN van .sw-home-hero/.sw-phz-stage zelf
       (position:sticky;top:0 in de CSS) tijdens die crossfade. Een bekende
       WebKit-bug laat position:sticky stil niet pinnen zodra een voorouder
       een niet-"visible" overflow heeft (van oudsher body{overflow-x:hidden},
       zie layout/theme.liquid -- daar inmiddels ook gefixt met overflow-x:
       clip, maar oudere iOS-versies zonder clip-support houden het oude
       gedrag). We detecteren dit RUNTIME: CSS.supports("position: sticky")
       zegt overal "true", ook op een motor waar de toepassing faalt, dus we
       meten het daadwerkelijke pin-gedrag (wijkt de sticky-top af van 0
       terwijl de stage middenin zijn scrollbereik zit?). Zodra gedetecteerd
       neemt dezelfde rAF-loop het pinnen over (position:fixed in het bereik,
       position:absolute;bottom:0 erna). Blijft ongebruikt -- dus nul effect
       -- zolang sticky native werkt (Chromium/Firefox/moderne WebKit). ---- */
    var stageStates = stages.map(function (el) {
      return { el: el, stickyChecked: false, stickyBroken: false };
    });

    function pinnedEl(el) {
      return el.querySelector(".sw-home-hero, .sw-phz-stage");
    }

    function checkStickySupport(state, rect, vh) {
      if (state.stickyChecked) return;
      /* Alleen keuren als de stage ruim middenin het scrollbereik zit -- vlak
         bij het begin/eind is een kleine afwijking normaal (het aan/uit-
         klikken van sticky zelf) en geen betrouwbaar signaal. */
      if (rect.top > -40 || rect.bottom < vh + 40) return;
      state.stickyChecked = true;
      var hero = pinnedEl(state.el);
      if (!hero) return;
      var heroTop = hero.getBoundingClientRect().top;
      state.stickyBroken = Math.abs(heroTop) > 2;
    }

    function applyStickyFallback(state, rect, vh) {
      var hero = pinnedEl(state.el);
      if (!hero) return;
      if (rect.top > 0) {
        /* Wrapper nog niet bereikt: de eigen (CSS-)positie, zoals sticky
           vóór het "vastklikken". */
        hero.style.position = "";
        hero.style.top = "";
        hero.style.bottom = "";
        hero.style.left = "";
        hero.style.right = "";
      } else if (rect.bottom > vh) {
        /* Middenin het scrollbereik: vastgepind aan de viewport-top, exact
           wat position:sticky;top:0 hoort te doen. */
        hero.style.position = "fixed";
        hero.style.top = "0";
        hero.style.left = "0";
        hero.style.right = "0";
        hero.style.bottom = "";
      } else {
        /* Wrapper scrollt uit beeld: loslaten aan de onderkant van de
           (position:relative) wrapper, zoals sticky vanzelf weer "unstickt"
           zodra de bodem van de wrapper in zicht komt. */
        hero.style.position = "absolute";
        hero.style.top = "";
        hero.style.bottom = "0";
        hero.style.left = "0";
        hero.style.right = "0";
      }
    }

    var ticking = false;

    function update() {
      ticking = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      stageStates.forEach(function (state) {
        var el = state.el;
        var rect = el.getBoundingClientRect();
        var scrollable = Math.max(1, rect.height - vh);
        var progress = Math.min(1, Math.max(0, -rect.top / scrollable));
        el.style.setProperty("--sw-zoom-progress", progress.toFixed(4));

        checkStickySupport(state, rect, vh);
        if (state.stickyBroken) applyStickyFallback(state, rect, vh);

        var copyEl = el.querySelector("[data-sw-hero-copy]");
        if (copyEl) {
          if (progress >= COPY_HIDE_THRESHOLD) {
            copyEl.setAttribute("data-sw-copy-hidden", "true");
          } else {
            copyEl.removeAttribute("data-sw-copy-hidden");
          }
        }
      });
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    update();
  }

  /* ---- 6) Koopchip OOS-scroll (kaart #118, Apple-banner-hero-ronde) ----
     EENMALIG geregistreerd (buiten init()) op document-niveau: init()
     draait opnieuw bij elk shopify:section:load, een listener hier zou
     dan bij iedere reload nog een keer toegevoegd worden (stacking). Event
     delegation vangt sowieso nieuw ingevoegde secties af, dus eenmalig
     registreren is voldoende en veiliger. ---- */
  document.addEventListener("click", function (e) {
    var btn = e.target.closest("[data-sw-chip-oos-scroll]");
    if (!btn) return;
    var targetId = btn.getAttribute("data-sw-chip-oos-target");
    var target = targetId && document.getElementById(targetId);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
  });

  /* ---- 7) Product-subnav (kaart #118, subnav-ronde) ----
     Eén init-functie behandelt ALLE [data-sw-subnav]-instanties op de
     pagina (er is er normaliter maar 1 per productpagina, maar dit blijft
     correct als er ooit meer dan 1 landing-sectie op 1 pagina staat). ---- */
  function initProductSubnav() {
    var bars = Array.prototype.slice.call(document.querySelectorAll("[data-sw-subnav]"));
    if (!bars.length) return;

    bars.forEach(function (bar) {
      if (bar.getAttribute("data-sw-subnav-ready") === "true") return;
      bar.setAttribute("data-sw-subnav-ready", "true");

      var heroId = bar.getAttribute("data-sw-subnav-hero");
      var hero = heroId && document.getElementById(heroId);

      /* ---- Ankers zonder bestaand doel verbergen (toekomstvast: geen
         aparte per-sectie boolean nodig voor Vergelijk/Combi deals). ---- */
      var links = Array.prototype.slice.call(bar.querySelectorAll("[data-sw-subnav-link]"));
      var validLinks = [];
      links.forEach(function (link) {
        var targetId = link.getAttribute("data-sw-subnav-target");
        var target = targetId && document.getElementById(targetId);
        if (target) {
          validLinks.push({ link: link, target: target });
        } else {
          /* Verberg de HELE <li> (de echte flex-item van .sw-subnav__links),
             niet alleen de <a>: anders blijft er een lege, onzichtbare
             flex-slot over die de gap aan weerszijden blijft meetellen,
             met een dubbele tussenruimte rond het verborgen anker tot
             gevolg (gemeten: ~40px i.p.v. 20px rond "Vergelijk" op
             productpagina's zonder sw-hub-compare-sec). */
          (link.closest("li") || link).setAttribute("hidden", "hidden");
        }
      });

      /* ---- Toon/verberg de balk + koopchip + header-toggle op dezelfde
         drempel als voorheen de sticky cta-bar (hero-stage boven de
         viewport verdwenen). ---- */
      if (hero) {
        var sectionId = heroId.replace(/^sw-herozoom-/, "");
        var chip = document.getElementById("sw-phz-chip-" + sectionId);
        var header = document.querySelector(".shopify-section-header");

        var barIO = new IntersectionObserver(function (entries) {
          var scrolledPastTop = entries[0].boundingClientRect.bottom < 0;
          var barVisible = !entries[0].isIntersecting && scrolledPastTop;
          bar.classList.toggle("sw-subnav--visible", barVisible);
          if (chip) chip.setAttribute("data-sw-chip-hidden", barVisible ? "true" : "false");
          if (header) header.classList.toggle("sw-subnav-hides-header", barVisible);
        }, { threshold: 0, rootMargin: "0px 0px -10% 0px" });
        barIO.observe(hero);
      } else {
        /* Geen hero-stage gevonden (defensief): toon de balk gewoon
           altijd, beter dan een balk die nooit verschijnt. */
        bar.classList.add("sw-subnav--visible");
      }

      /* ---- Scroll-spy: markeer de link van de sectie die het dichtst
         bovenin de viewport staat als actief. ---- */
      if (validLinks.length && "IntersectionObserver" in window) {
        var setActive = function (targetEl) {
          validLinks.forEach(function (entry) {
            entry.link.classList.toggle("sw-subnav__link--active", entry.target === targetEl);
          });
        };

        var spyIO = new IntersectionObserver(function (entries) {
          var visible = entries
            .filter(function (e) { return e.isIntersecting; })
            .sort(function (a, b) { return a.boundingClientRect.top - b.boundingClientRect.top; });
          if (visible.length) setActive(visible[0].target);
        }, { threshold: 0, rootMargin: "-30% 0px -60% 0px" });

        validLinks.forEach(function (entry) { spyIO.observe(entry.target); });
      }

      /* ---- Smooth scroll met ruimte voor de vaste balk (scroll-margin-
         top staat ook in CSS als vangnet voor middenklik/directe hash-
         navigatie; dit hier dekt het normale klikpad). ---- */
      validLinks.forEach(function (entry) {
        entry.link.addEventListener("click", function (e) {
          e.preventDefault();
          var barHeight = bar.getBoundingClientRect().height || 56;
          var top = entry.target.getBoundingClientRect().top + window.pageYOffset - barHeight - 12;
          window.scrollTo({ top: top, behavior: reduceMotion ? "auto" : "smooth" });
        });
      });
    });
  }

  /* ---- 5) Hero-fold-offset (kaart #161) ----
     Til de hero-copy + koopchip (sw-product-herozoom.liquid) omhoog naar de
     onderrand van de EERSTE viewport, zodat titel + koopknop al bij het laden
     zichtbaar zijn. De offset = de ruimte boven de hero (theme-header +
     kruimelpad); we meten die per zoomstage en zetten 'm als --sw-phz-fold, dat
     de CSS optelt bij de bodem-marge van copy/chip. De stage zelf blijft 100svh
     (dus de sticky scroll-zoom houdt GEEN lege strook onderaan); zonder deze JS
     valt de CSS terug op 0px = het oude gedrag. Opnieuw meten op resize/load,
     want de header-hoogte verschilt per breakpoint. Geldt voor ELKE hero (zoom
     aan of uit), dus we selecteren .sw-phz-zoomstage i.p.v. [data-sw-scrollzoom]. */
  function initHeroFold() {
    var heroes = Array.prototype.slice.call(document.querySelectorAll(".sw-phz-zoomstage"));
    if (!heroes.length) return;

    function measure() {
      heroes.forEach(function (el) {
        var offset = Math.max(0, Math.round(el.getBoundingClientRect().top + window.pageYOffset));
        el.style.setProperty("--sw-phz-fold", offset + "px");
      });
    }

    measure();
    if (!window.__swHeroFoldBound) {
      window.__swHeroFoldBound = true;
      window.addEventListener("resize", measure, { passive: true });
      window.addEventListener("load", measure, { passive: true });
    }
  }

  /* ---- 8) Subnav-hoogte (kaart #176, ronde 2) ----
     Zie doc-comment bovenaan het bestand. Defensief: doet niets als er geen
     subnav op de pagina staat; ResizeObserver dekt wrap-wijzigingen door
     resize EN door layoutverschuivingen (bv. kleurenrij die meewrapt),
     resize-listener is de fallback voor browsers zonder ResizeObserver. */
  /* ---- 7b) Tweeregelige balk zodra één regel niet past (kaart #295) ----
     Meet de breedte die titel + links + Koop-groep samen nodig hebben tegen de
     beschikbare breedte binnen .sw-subnav__inner, en zet .sw-subnav--stacked
     als het niet past. Bewust een meting en geen media-query: het hangt af van
     het aantal zichtbare links (8 of 9), de lengte van de productnaam en of er
     variantchips in de balk staan -- dat verschilt per product.

     Meet altijd eerst in de een-regel-stand (class eraf), anders meet je de
     gestapelde breedtes en klapt de balk heen en weer.

     Geen ResizeObserver op de balk zelf: deze functie verandert de HOOGTE van
     de balk, dus die zou zichzelf in een lus observeren. De breedte verandert
     alleen bij een viewport-resize, dus daar luisteren we op. */
  function initSubnavFit() {
    var bar = document.querySelector("[data-sw-subnav]");
    if (!bar) return;
    if (bar.getAttribute("data-sw-subnav-fit-ready") === "true") return;
    bar.setAttribute("data-sw-subnav-fit-ready", "true");

    var inner = bar.querySelector(".sw-subnav__inner");
    var title = bar.querySelector(".sw-subnav__title");
    var links = bar.querySelector(".sw-subnav__links");
    var end = bar.querySelector(".sw-subnav__end");
    if (!inner || !links) return;

    function fit() {
      bar.classList.remove("sw-subnav--stacked");

      var cs = window.getComputedStyle(inner);
      var padding = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      var kolomGap = parseFloat(cs.columnGap) || 0;
      var beschikbaar = inner.getBoundingClientRect().width - padding;
      var nodig = (title ? title.scrollWidth : 0) +
                  links.scrollWidth +
                  (end ? end.scrollWidth : 0) +
                  kolomGap * 2;

      if (nodig > beschikbaar) bar.classList.add("sw-subnav--stacked");

      /* Zelf bijwerken in plaats van wachten op de ResizeObserver van
         initSubnavHeight: die bleek de sprong van 56px naar 96px niet op te
         pikken, waardoor scroll-margin-top op 72px bleef staan en een anker
         24px onder de balk uitkwam. */
      document.documentElement.style.setProperty(
        "--sw-subnav-h",
        Math.round(bar.getBoundingClientRect().height) + "px"
      );
    }

    fit();
    /* Webfonts veranderen de tekstbreedtes; opnieuw meten zodra ze er zijn. */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
    window.addEventListener("resize", fit, { passive: true });
  }

  function initSubnavHeight() {
    var bar = document.querySelector("[data-sw-subnav]");
    if (!bar) return;
    /* init() draait ook opnieuw op shopify:section:load -- guard tegen een
       nieuwe ResizeObserver per herlaadbeurt, zelfde patroon als
       data-sw-subnav-ready in initProductSubnav hierboven. */
    if (bar.getAttribute("data-sw-subnav-h-ready") === "true") return;
    bar.setAttribute("data-sw-subnav-h-ready", "true");

    function measure() {
      var h = Math.round(bar.getBoundingClientRect().height) || 96;
      document.documentElement.style.setProperty("--sw-subnav-h", h + "px");
    }

    measure();
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(measure);
      ro.observe(bar);
    } else if (!window.__swSubnavHeightBound) {
      window.__swSubnavHeightBound = true;
      window.addEventListener("resize", measure, { passive: true });
    }
  }

  function init() {
    initReveal();
    initParallax();
    initScrollZoom();
    initHeroFold();
    initProductSubnav();
    initSubnavHeight();
    initSubnavFit();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  /* Shopify theme-editor: secties kunnen live opnieuw laden — reveal
     opnieuw toepassen op nieuw ingevoegde elementen. */
  document.addEventListener("shopify:section:load", init);
})();
